/**
 * Blocky Striker — client constants. 3D voxel soccer endless-runner (Warmup side-game).
 * Spec: specs/blocky-striker/spec.md · Fidelity source: specs/blocky-striker/handoff/README.md.
 *
 * Display economy figures mirror server/src/constants/blockyStriker (server is source of truth).
 * Colors follow the handoff's design tokens (Zporti purple family), distinct from Striker Run's lime.
 */

import { AVATAR_IDS, type AvatarId } from '../../../constants/avatarIds';
import { MONKEY_AVATAR_ID } from '../../../constants/monkey';

export const BLOCKY_STRIKER_GAME_KEY = 'BLOCKY_STRIKER';

/** Display figures (server authoritative). Mirror server/src/constants/blockyStriker. */
export const BLOCKY_STRIKER_COINS_PER_GOAL = 5;
export const BLOCKY_STRIKER_MAX_PAID_GOALS_PER_RUN = 6;
export const BLOCKY_STRIKER_MAX_PAID_PICKUPS_PER_RUN = 15;
export const BLOCKY_STRIKER_DAILY_COIN_CAP = 50;

/** Card accent for the hub tile — the game's primary action purple (handoff "Zporti purple"). */
export const BLOCKY_STRIKER_ACCENT = '#A78BFA';
export const BLOCKY_STRIKER_CARD_GRADIENT: [string, string] = ['#1D1435', '#120C22'];

/** Inline SVG card art — voxel pitch at dusk, blocky striker, goal gate, floating coin. */
export const BLOCKY_STRIKER_CARD_ART_URI = `data:image/svg+xml,${encodeURIComponent(`<svg viewBox="0 0 400 280" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#171029"/><stop offset="55%" stop-color="#241b3a"/><stop offset="100%" stop-color="#2f8d3e"/></linearGradient><linearGradient id="pit" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#2c883b"/><stop offset="100%" stop-color="#3aa049"/></linearGradient><radialGradient id="gl" cx=".5" cy=".45"><stop offset="0%" stop-color="#A78BFA" stop-opacity=".45"/><stop offset="100%" stop-color="#A78BFA" stop-opacity="0"/></radialGradient></defs><rect width="400" height="280" fill="url(#sky)" rx="16"/><rect y="150" width="400" height="130" fill="url(#pit)"/><g fill="#379a46" opacity=".65"><rect y="150" width="400" height="22"/><rect y="194" width="400" height="22"/><rect y="238" width="400" height="22"/></g><g stroke="#e8e8e8" stroke-width="5" opacity=".8"><line x1="70" y1="150" x2="18" y2="280"/><line x1="330" y1="150" x2="382" y2="280"/></g><circle cx="200" cy="150" r="86" fill="url(#gl)"/><g fill="#f4f4f4"><rect x="150" y="96" width="7" height="46"/><rect x="243" y="96" width="7" height="46"/><rect x="150" y="90" width="100" height="8"/></g><rect x="157" y="98" width="86" height="40" fill="#ffffff" opacity=".14"/><g transform="translate(122,196)"><rect x="-16" y="-14" width="32" height="28" fill="#6B46C1"/><rect x="-16" y="-2" width="32" height="4" fill="#FCD34D"/></g><g transform="translate(287,214)"><rect x="-15" y="-13" width="30" height="26" fill="#8b5a2b"/><rect x="-15" y="-13" width="30" height="5" fill="#5e3c1c"/><rect x="-15" y="8" width="30" height="5" fill="#5e3c1c"/></g><g transform="translate(200,196)"><ellipse cy="42" rx="24" ry="6" fill="rgba(0,0,0,.35)"/><rect x="-9" y="6" width="8" height="24" fill="#3B82F6"/><rect x="2" y="6" width="8" height="24" fill="#3B82F6"/><rect x="-9" y="26" width="8" height="8" fill="#FFFFFF"/><rect x="2" y="26" width="8" height="8" fill="#FFFFFF"/><rect x="-13" y="-20" width="26" height="26" fill="#3B82F6"/><rect x="-13" y="4" width="26" height="5" fill="#FFFFFF"/><rect x="-19" y="-18" width="6" height="18" fill="#F0A868"/><rect x="13" y="-18" width="6" height="18" fill="#F0A868"/><circle cy="-34" r="16" fill="#4FA9DB"/><rect x="-8" y="-52" width="20" height="7" fill="#6B4423" transform="rotate(7)"/><circle cx="26" cy="8" r="10" fill="#f4f4f4"/><rect x="21" y="3" width="7" height="6" fill="#1a1a1a"/></g><circle cx="86" cy="70" r="13" fill="#FCD34D" stroke="#F59E0B" stroke-width="3"/><rect x="80" y="64" width="5" height="5" fill="#FDE68A"/><text x="200" y="52" text-anchor="middle" font-family="-apple-system,system-ui,sans-serif" font-size="19" font-weight="800" letter-spacing="2" fill="#A78BFA">ZPORTI</text></svg>`)}`;

export const BLOCKY_STRIKER_TEST_IDS = {
  view: 'blocky-striker-view',
  loading: 'blocky-striker-loading',
  error: 'blocky-striker-error',
  retry: 'blocky-striker-retry',
  back: 'blocky-striker-back',
} as const;

/** Cause of a run ending, as reported by the runtime (timer expiry or zero lives). */
/** 'clear' = a campaign level's objective was met (v4) — client-side only, never sent to the server. */
export type BlockyStrikerEndCause = 'time' | 'lives' | 'clear';

/** Messages posted from the WebView game runtime to the RN host over the bridge. */
export type BlockyStrikerBridgeMessage =
  | { type: 'ready' }
  | {
      type: 'run-end';
      points: number;
      coins: number;
      goals: number;
      distance: number;
      cause: BlockyStrikerEndCause;
    }
  | { type: 'load-error' };

/**
 * Five voxel colors an avatar drives on the in-game mascot (handoff §Avatars):
 * ball (head base), patch (head pentagons), hair (hat/hair blocks), jersey (torso+thighs),
 * trim (belt+socks).
 */
export type BlockyStrikerVoxelPalette = {
  ball: string;
  patch: string;
  hair: string;
  jersey: string;
  trim: string;
};

export type BlockyStrikerRosterId = AvatarId | typeof MONKEY_AVATAR_ID;

export type BlockyStrikerRosterEntry = {
  id: BlockyStrikerRosterId;
  /** Display name under the thumbnail (Kick Off grid). */
  name: string;
  /** R2 object key for the thumbnail image. */
  thumbPath: string;
  /** Resolve thumbPath through getSmallAssetUrl (-150 variant) vs getAssetUrl as-is. */
  thumbSmall: boolean;
  palette: BlockyStrikerVoxelPalette;
};

/**
 * Authoritative static voxel palettes — the CORS-safe replacement for runtime palette
 * sampling (handoff "Known open issue — avatar colors", spec Q-2).
 *
 * Sources: the 9 folder avatars + monkey use the handoff's hand-matched table verbatim
 * (design to supply a corrected JSON before avatars are called pixel-final); the three
 * complete-look girls (girl4-6) are NOT in the handoff — their palettes are placeholders
 * chosen from their illustrations' dominant kit colors, same Q-2 follow-up.
 */
export const BLOCKY_STRIKER_ROSTER: readonly BlockyStrikerRosterEntry[] = [
  {
    id: AVATAR_IDS.BLUE_WITH_HAIR,
    name: 'Blue Hair',
    thumbPath: 'assets/Avatars/BlueWithHair/idle.webp',
    thumbSmall: true,
    palette: { ball: '#4FA9DB', patch: '#5C4033', hair: '#6B4423', jersey: '#3B82F6', trim: '#FFFFFF' },
  },
  {
    id: AVATAR_IDS.MOYKAN_BALL,
    name: 'Moykan',
    thumbPath: 'assets/Avatars/MoykanBall/idle.webp',
    thumbSmall: true,
    palette: { ball: '#F4F4F4', patch: '#1A1A1A', hair: '#1E7A46', jersey: '#1E7A46', trim: '#F4F4F4' },
  },
  {
    id: AVATAR_IDS.BLUE_WITH_CAP,
    name: 'Blue Cap',
    thumbPath: 'assets/Avatars/BlueWithCap/idle.webp',
    thumbSmall: true,
    palette: { ball: '#F4F4F4', patch: '#2563EB', hair: '#1E4FB8', jersey: '#2563EB', trim: '#FCD34D' },
  },
  {
    id: AVATAR_IDS.GREEN_WHITE,
    name: 'Green White',
    thumbPath: 'assets/Avatars/GreenWhite/idle.webp',
    thumbSmall: true,
    palette: { ball: '#F4F4F4', patch: '#1E7A46', hair: '#166534', jersey: '#1E7A46', trim: '#FFFFFF' },
  },
  {
    id: AVATAR_IDS.LIGHT_BLUE,
    name: 'Light Blue',
    thumbPath: 'assets/Avatars/LightBlue/idle.webp',
    thumbSmall: true,
    palette: { ball: '#8ED4F5', patch: '#3B82C4', hair: '#6B4423', jersey: '#7DD3FC', trim: '#FFFFFF' },
  },
  {
    id: AVATAR_IDS.LIGHT_RED,
    name: 'Light Red',
    thumbPath: 'assets/Avatars/LightRed/idle.webp',
    thumbSmall: true,
    palette: { ball: '#E8636F', patch: '#B93A47', hair: '#1F1F1F', jersey: '#E8636F', trim: '#FFFFFF' },
  },
  {
    id: AVATAR_IDS.WHITE_RED,
    name: 'White Red',
    thumbPath: 'assets/Avatars/WhiteRed/idle.webp',
    thumbSmall: true,
    palette: { ball: '#F4F4F4', patch: '#EF4444', hair: '#C0392B', jersey: '#FFFFFF', trim: '#EF4444' },
  },
  {
    id: AVATAR_IDS.WHITE_YELLOW,
    name: 'White Gold',
    thumbPath: 'assets/Avatars/WhiteYellow/idle.webp',
    thumbSmall: true,
    palette: { ball: '#F2EFE4', patch: '#1A1A1A', hair: '#F2C230', jersey: '#F2C230', trim: '#1A1A1A' },
  },
  {
    id: AVATAR_IDS.MAC,
    name: 'MAC',
    thumbPath: 'assets/Avatars/MAC/idle.webp',
    thumbSmall: true,
    palette: { ball: '#6B46C1', patch: '#FCD34D', hair: '#2D1B4E', jersey: '#6B46C1', trim: '#FCD34D' },
  },
  // Complete-look girls — single illustration, no -150 variant (see constants/avatarRegistry.ts).
  // Palettes are Q-2 placeholders (not in the handoff table).
  {
    id: AVATAR_IDS.ALL_STAR,
    name: 'All Star',
    thumbPath: 'assets/character/char/girl4.webp',
    thumbSmall: false,
    palette: { ball: '#F4F4F4', patch: '#EC4899', hair: '#6B4423', jersey: '#EC4899', trim: '#FFFFFF' },
  },
  {
    id: AVATAR_IDS.TRAILBLAZER,
    name: 'Trailblazer',
    thumbPath: 'assets/character/char/girl5.webp',
    thumbSmall: false,
    palette: { ball: '#F4F4F4', patch: '#0F766E', hair: '#1F1F1F', jersey: '#14B8A6', trim: '#FDE68A' },
  },
  {
    id: AVATAR_IDS.SKY_CAPTAIN,
    name: 'Sky Captain',
    thumbPath: 'assets/character/char/girl6.webp',
    thumbSmall: false,
    palette: { ball: '#F4F4F4', patch: '#0284C7', hair: '#F2C230', jersey: '#38BDF8', trim: '#FFFFFF' },
  },
  {
    id: MONKEY_AVATAR_ID,
    name: 'Monkey',
    // Win-150 exists on the CDN and is the exact art the handoff bundles for this thumb.
    thumbPath: 'assets/Avatars/Monkey/Win-150.webp',
    thumbSmall: false,
    palette: { ball: '#8B5A2B', patch: '#5E3C1C', hair: '#4A2F14', jersey: '#8B5A2B', trim: '#FCD34D' },
  },
] as const;

/** Game-over mascot art (monkey "win" pose, 96×96 in the card — handoff §Game over). */
export const BLOCKY_STRIKER_GAME_OVER_ART_PATH = 'assets/Avatars/Monkey/Win-150.webp';

/** Pure palette lookup — deterministic, defaults to the first roster entry (T-13). */
export function getBlockyStrikerPalette(id: string | null | undefined): BlockyStrikerVoxelPalette {
  const entry = BLOCKY_STRIKER_ROSTER.find((r) => r.id === id);
  return (entry ?? BLOCKY_STRIKER_ROSTER[0]).palette;
}
